<template>
  <div class="flex flex-col">
    <div
      class="flex flex-row h-[40px] items-center gap-4 text-neutral-500 mb-4"
    >
      <div class="size-5">
        <Building2 />
      </div>
      <div class="w-full text-base font-semibold">
        Everyone at “Workspace” can edit this file.
      </div>
    </div>
    <div class="flex flex-row h-[40px] items-center gap-4">
      <div>
        <svg-icon
          icon="app-icons/globe"
          width="40"
          height="40"
          fill="#E5E5E5"
        />
      </div>
      <div class="w-full">
        Anyone with the link<br />
        <a
          v-if="isClipboardSupported"
          href="#"
          @click.stop.prevent="copyToClipboard"
          >copy link</a
        >
      </div>
      <div class="md:min-w-36 hidden sm:block">
        <Select
          :model-value="publicLinkModel ?? 'no-access'"
          default-value="no-access"
          :disabled="!canEdit || isPending"
          @update:model-value="onChangePublicLink"
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="design">can view &amp; play</SelectItem>
            <SelectItem value="play mode">can play only</SelectItem>
            <SelectItem value="no-access">has no access</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { snakeCase } from 'lodash';
import { useMutation } from '@tanstack/vue-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/components/select';
import { useClipboard } from '@vueuse/core';
import { toast } from 'vue-sonner';

import requests from '@vuex/requests';
import SvgIcon from '@ui/components/SvgIcon.vue';
import { Building2 } from 'lucide-vue-next';

import mixpanelSingleton from '@/mixpanel/mixpanelSingleton.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import { useProtect } from '@/composables/useProtect.js';

const store = useStore();
const { copy, copied, isSupported: isClipboardSupported } = useClipboard();

watch(copied, newValue => {
  if (newValue) {
    toast.success('Project Link copied!');
  }
});

const { hasPermission: canEdit } = useProtect({
  permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
});

// TODO: change way of getting public link
const publicLink = computed(() => store.getters['project/getPublicLink']);
const publicLinkModel = ref(publicLink.value);
watch(publicLink, newValue => {
  publicLinkModel.value = newValue;
});

const openProjectHash = computed(() => store.getters['user/projects/getOpen']);

const { isPending, mutate: changePublicLink } = useMutation({
  mutationFn: async payload => {
    await requests.projects.updatePublicLink(payload);
  },
  onSuccess: async (data, payload) => {
    store.commit('project/setPublicLink', { value: payload.publicLink });
    // Mix Panel Event
    if (payload.publicLink !== null) {
      mixpanelSingleton.track('button_clicked', {
        type: 'share',
        subtype: 'public_link',
        target: snakeCase(payload.publicLink),
      });
    }
  },
});

const onChangePublicLink = value => {
  changePublicLink({
    hash: openProjectHash.value,
    publicLink: value === 'no-access' ? null : value,
  });
};

/**
 * Copy the project url to the clipboard
 */
function copyToClipboard() {
  let link = window.location.origin + window.location.pathname;
  if (publicLinkModel.value === 'play mode') {
    link += '/play';
  }
  copy(link);
}
</script>
